import { DisplayError } from "@/components/Error/Error";
import { CompleteSvg, TodoSvg } from "@/components/Icon/Icon";
import { Logo } from "@/components/Logo/Logo";
import { POSTHOG_VISIBLE } from "@/constants/posthog";
import { useOrg } from "@/hooks/org";
import { EMAIL_VERIFIED_POLLING_INTERVAL, useUserEmailVerified } from "@/hooks/userEmailVerified";
import { OnboardingCard } from "@/pages/Onboarding/Components/OnboardingCard";
import {
  useCancelBaaV1Mutation,
  useInitiateBaaV1Mutation,
  useVerifyEmailApiV1UsersUserIdVerifyEmailPostMutation
} from "@/store/baseClientApi";
import { useGetOrgConsentStatusV1Query } from "@/store/clientApi";
import { selectRegion } from "@/store/slices/orgSlice";
import { RootState } from "@/store/store";
import { Box, Link, Skeleton, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

type ResendStatus = "Sending" | "Success" | "Error" | undefined;

interface ChecklistItemProps {
  completed: boolean;
  loading?: boolean;
  label: string;
  resendStatus: ResendStatus;
  onResend: () => Promise<void>;
  successMessage: string;
  sendingMessage: string;
  errorMessage: string;
}

const ChecklistItem: FC<ChecklistItemProps> = ({
  completed,
  loading,
  label,
  resendStatus,
  onResend,
  successMessage,
  sendingMessage,
  errorMessage
}) => {
  const { t } = useTranslation("onboarding");
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
      <Box
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          marginTop: "2px",
          color: completed ? theme.palette.success.dark : theme.palette.warning.main
        }}
      >
        {loading ? (
          <Skeleton variant="circular" width="24px" height="24px" />
        ) : completed ? (
          <CompleteSvg height="24px" width="24px" />
        ) : (
          <TodoSvg height="24px" width="24px" />
        )}
      </Box>
      <Box sx={{ marginTop: "4px" }}>
        {loading ? (
          <>
            <Skeleton variant="rounded" width="250px" height="1em" />
            <Skeleton variant="rounded" width="100px" height="0.875em" sx={{ marginTop: "8px" }} />
          </>
        ) : (
          <>
            <Typography variant="body2" {...POSTHOG_VISIBLE}>
              {label}
            </Typography>
            {!completed && (
              <Typography color={theme.surface.onVariant} fontSize="0.875em" sx={{ marginTop: "4px" }} {...POSTHOG_VISIBLE}>
                {resendStatus === "Success" ? (
                  successMessage
                ) : resendStatus === "Sending" ? (
                  sendingMessage
                ) : resendStatus === "Error" ? (
                  errorMessage
                ) : (
                  <Link sx={{ fontWeight: "bold" }} component="button" underline="hover" onClick={onResend} {...POSTHOG_VISIBLE}>
                    {t("message.resendLink")}
                  </Link>
                )}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

/**
 * Page-level component that blocks user progress until their Auth0 email address is verified
 * and, for US-region organisations, the Business Associate Agreement is signed.
 *
 * @component
 */
export const AwaitingEmailVerification: FC = () => {
  const [searchParams] = useSearchParams();
  const [emailResendStatus, setEmailResendStatus] = useState<ResendStatus>();
  const [baaResendStatus, setBaaResendStatus] = useState<ResendStatus>();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("onboarding");
  const redirectRoute = searchParams.get("target");

  const [resendEmail] = useVerifyEmailApiV1UsersUserIdVerifyEmailPostMutation();
  const [cancelBaa] = useCancelBaaV1Mutation();
  const [initiateBaa] = useInitiateBaaV1Mutation();

  // We need to fetch the org to determine the region,
  // which determines if we need to check for BAA acceptance as well as email verification.
  const { isLoading: orgLoading } = useOrg();

  const { userId, emailVerified: isEmailVerified, isLoading: emailLoading, error: emailError } = useUserEmailVerified();
  const region = useSelector((state: RootState) => selectRegion(state));
  const isUS = region === "US";

  const {
    data: consentStatuses,
    isLoading: consentLoading,
    error: consentError
  } = useGetOrgConsentStatusV1Query(undefined, { skip: !isUS, pollingInterval: EMAIL_VERIFIED_POLLING_INTERVAL });

  const consentLoaded = !consentLoading && consentStatuses != null;
  const isBaaAccepted = consentStatuses?.find((s) => s.document.document_type === "baa")?.status === "accepted";
  const showBaa = isUS;
  const baaLoading = isUS && !consentLoaded;

  const isAllComplete = isEmailVerified && (!isUS || isBaaAccepted);
  const isLoading = emailLoading || consentLoading || orgLoading;

  useEffect(() => {
    if (isAllComplete) {
      setTimeout(() => {
        window.location.href = redirectRoute || "/cases"; // navigate using the browser so auth0 tokens get refeshed
      }, 2500);
    }
  }, [isAllComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResendEmail = async () => {
    if (!userId) {
      setEmailResendStatus("Error");
      return;
    }

    setEmailResendStatus("Sending");

    try {
      await resendEmail({ userId }).unwrap();
      setEmailResendStatus("Success");
    } catch (_) {
      setEmailResendStatus("Error");
    }
  };

  const handleResendBaa = async () => {
    setBaaResendStatus("Sending");

    try {
      await cancelBaa().unwrap();
      await initiateBaa().unwrap();
      setBaaResendStatus("Success");
    } catch (_) {
      setBaaResendStatus("Error");
    }
  };

  const error = emailError || consentError;
  if (error) {
    return <DisplayError error={error} />;
  }

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        height: "max-content",
        minHeight: "100vh",
        padding: sm ? "0" : "80px",
        overflowY: "auto",
        backgroundColor: (theme: Theme) => theme.surface.normal
      }}
    >
      <OnboardingCard>
        <Box
          component="div"
          sx={{
            height: "auto",
            marginBottom: "42px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Logo width="171px" />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "16px" }}>
          <Typography variant="subtitle1" sx={{ color: theme.surface.onVariant }} {...POSTHOG_VISIBLE}>
            {t("message.checkEmailTo")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "1em" }}>
            <ChecklistItem
              completed={isEmailVerified}
              label={t("message.verifyEmailAddress")}
              loading={isLoading}
              resendStatus={emailResendStatus}
              onResend={handleResendEmail}
              successMessage={t("message.emailResendSuccess")}
              sendingMessage={t("message.sendingEmail")}
              errorMessage={t("message.resendEmailError")}
            />

            {(showBaa || isLoading) && (
              <ChecklistItem
                completed={isBaaAccepted}
                loading={baaLoading || isLoading}
                label={t("message.signBaa")}
                resendStatus={baaResendStatus}
                onResend={handleResendBaa}
                successMessage={t("message.baaResendSuccess")}
                sendingMessage={t("message.sendingBaaEmail")}
                errorMessage={t("message.resendBaaError")}
              />
            )}
          </Box>

          {isAllComplete && !isLoading && (
            <Typography variant="body2" color={theme.surface.onVariant} {...POSTHOG_VISIBLE}>
              {t("message.redirecting")}
            </Typography>
          )}
        </Box>
      </OnboardingCard>
    </Box>
  );
};
